
import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Home, Compass, BookOpen } from "lucide-react";
import SEOMetadata from "@/components/common/SEOMetadata";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  // Attempt to find a relevant redirect based on the path
  const suggestAlternative = () => {
    const path = location.pathname.toLowerCase();
    
    // Check for common tour paths
    if (path.includes('brooklyn') || path.includes('heights')) {
      return '/tours/brooklyn-heights';
    }
    if (path.includes('financial') || path.includes('district')) {
      return '/tours/financial-district';
    }
    if (path.includes('lower') || path.includes('east') || path.includes('side')) {
      return '/tours/lower-east-side';
    }
    if (path.includes('private') || path.includes('custom')) {
      return '/tours/private-tours';
    }
    
    // Check for other main sections
    if (path.includes('book') || path.includes('reserve')) {
      return '/booking';
    }
    if (path.includes('galler') || path.includes('photo')) {
      return '/gallery';
    }
    if (path.includes('blog') || path.includes('post')) {
      return '/blog';
    }
    if (path.includes('about')) {
      return '/about';
    }
    if (path.includes('contact')) {
      return '/contact';
    }
    if (path.includes('press') || path.includes('media')) {
      return '/press-kit';
    }
    if (path.includes('news')) {
      return '/in-the-news';
    }
    
    // Default to home
    return '/';
  };

  const suggestedPath = suggestAlternative();

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDF6ED] px-4">
      <SEOMetadata
        title="Page Not Found | 404 Error"
        description="Sorry, the page you are looking for doesn't exist or has been moved."
        noindex={true}
        canonicalUrl="https://catsabouttowntours.com/404"
      />

      <div className="text-center p-8 max-w-2xl">
        <div className="mb-8">
          <img 
            src="/lovable-uploads/a7814c71-1569-43b8-8a9c-e24f811be427.png" 
            alt="Curious cat" 
            className="w-32 h-32 mx-auto mb-4 animate-bounce"
          />
          <h1 className="text-6xl font-bold mb-4 text-[#8B4513]">404</h1>
          <p className="text-xl text-[#3E2723] mb-2">Oops! This page seems to have wandered off...</p>
          <p className="text-[#3E2723]/80 mb-8">Like a curious cat, it might have found a new hiding spot.</p>
        </div>

        {suggestedPath !== '/' && (
          <div className="mb-6 p-4 bg-[#EADDCA] rounded-lg">
            <p className="text-[#3E2723]">Looking for something similar? Try:</p>
            <Link to={suggestedPath} className="text-[#8B4513] underline font-medium hover:text-[#C46829]">
              {suggestedPath.charAt(0).toUpperCase() + suggestedPath.slice(1).replace(/-/g, ' ')}
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link 
            to="/" 
            className="flex flex-col items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
          >
            <Home className="w-6 h-6 text-[#8B4513] mb-2" />
            <span className="text-[#3E2723]">Home</span>
          </Link>
          <Link 
            to="/tours" 
            className="flex flex-col items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
          >
            <Compass className="w-6 h-6 text-[#8B4513] mb-2" />
            <span className="text-[#3E2723]">Our Tours</span>
          </Link>
          <Link 
            to="/blog" 
            className="flex flex-col items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
          >
            <BookOpen className="w-6 h-6 text-[#8B4513] mb-2" />
            <span className="text-[#3E2723]">Blog</span>
          </Link>
        </div>

        <Link 
          to="/" 
          className="inline-flex items-center px-6 py-3 bg-[#8B4513] text-white rounded-lg hover:bg-[#8B4513]/90 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
          <Home className="w-5 h-5 mr-2" />
          Return to Home
        </Link>
      </div>
    </div>
  );
};

export default NotFound;
